import React, { useState, useEffect } from 'react';
import { Person, LegalEntity } from '../types/Contract';
import './PersonSelector.css';

interface PersonSelectorProps {
  type: 'seller' | 'buyer';
  persons: (Person | LegalEntity)[];
  selectedPerson: Person | LegalEntity | null;
  onPersonSelect: (person: Person | LegalEntity | null) => void;
  placeholder?: string;
}

export const PersonSelector: React.FC<PersonSelectorProps> = ({
  type,
  persons,
  selectedPerson,
  onPersonSelect,
  placeholder = 'Поиск или выбор из списка...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredPersons, setFilteredPersons] = useState<(Person | LegalEntity)[]>([]);

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredPersons(persons);
    } else {
      const filtered = persons.filter(person => {
        const term = searchTerm.toLowerCase();

        if ((person as any).type === 'legal_entity') {
          const legal = person as LegalEntity;
          const name = legal.name.toLowerCase();
          const inn = legal.inn.toLowerCase();
          return name.includes(term) || inn.includes(term);
        } else {
          const p = person as Person;
          const fullName = `${p.lastName} ${p.firstName} ${p.middleName || ''}`.toLowerCase();
          const passport = `${p.passportSeries} ${p.passportNumber}`.toLowerCase();
          return fullName.includes(term) || passport.includes(term);
        }
      });
      setFilteredPersons(filtered);
    }
  }, [searchTerm, persons]);

  const handlePersonSelect = (person: Person | LegalEntity) => {
    onPersonSelect(person);
    setIsOpen(false);
    setSearchTerm(getDisplayName(person));
  };

  const handleClearSelection = () => {
    onPersonSelect(null);
    setSearchTerm('');
  };

  const getDisplayName = (person: Person | LegalEntity) => {
    if ((person as any).type === 'legal_entity') {
      return (person as LegalEntity).name;
    }
    const p = person as Person;
    return `${p.lastName} ${p.firstName} ${p.middleName || ''}`.trim();
  };

  const getPersonDetails = (person: Person | LegalEntity) => {
    if ((person as any).type === 'legal_entity') {
      const legal = person as LegalEntity;
      return `ИНН: ${legal.inn}, ОГРН: ${legal.ogrn}`;
    }
    const p = person as Person;
    return `Паспорт: ${p.passportSeries} ${p.passportNumber}`;
  };

  useEffect(() => {
    if (selectedPerson) {
      setSearchTerm(getDisplayName(selectedPerson));
    } else {
      setSearchTerm('');
    }
  }, [selectedPerson]);

  return (
    <div className="person-selector">
      <div className="selector-input-wrapper">
        <input
          type="text"
          className="selector-input"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => setIsOpen(true)}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
        />
        {selectedPerson && (
          <button
            type="button"
            className="clear-button"
            onClick={handleClearSelection}
            title="Очистить выбор"
          >
            ×
          </button>
        )}
        <button
          type="button"
          className="dropdown-arrow"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? '▲' : '▼'}
        </button>
      </div>

      {isOpen && (
        <div className="dropdown-list">
          {filteredPersons.length === 0 ? (
            <div className="no-results">
              {persons.length === 0
                ? `Нет ${type === 'seller' ? 'продавцов' : 'покупателей'} в существующих договорах`
                : 'Не найдено совпадений'
              }
            </div>
          ) : (
            filteredPersons.map((person, index) => (
              <div
                key={index}
                className="dropdown-item"
                onClick={() => handlePersonSelect(person)}
              >
                <div className="person-name">{getDisplayName(person)}</div>
                <div className="person-details">{getPersonDetails(person)}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}; 